import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import BackMethods from "../../back_component/BackMethods";
import { FaFileExcel, FaSearch, FaTable, FaChartLine, FaClock, FaInfoCircle, FaCalculator, FaMoneyBillWave } from "react-icons/fa";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const PriceSurveyReportPage = () => {
    const [searchParams] = useSearchParams();
    const { backEnd_request } = BackMethods();

    const urlEstablishmentUid = searchParams.get("establishmentUid");
    const urlProductId = searchParams.get("productId");

    const [reports, setReports] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);

    const currencyOptions = [
        { value: "SYP", label: "ليرة سورية (SYP)" },
        { value: "USD", label: "دولار أمريكي ($)" }
    ];

    const [filters, setFilters] = useState({
        from: "",
        to: "",
        productId: urlProductId || "",
        establishmentUid: urlEstablishmentUid || "",
        currency: "SYP"
    });

    const isSearchDisabled = !filters.from || !filters.to;

    // حساب إحصائيات المواد (الوسطي)
    const productStats = useMemo(() => {
        if (!reports || reports.length === 0) return [];
        const stats = reports.reduce((acc, item) => {
            if (!acc[item.productName]) {
                acc[item.productName] = { sum: 0, count: 0 };
            }
            acc[item.productName].sum += item.priceValue;
            acc[item.productName].count += 1;
            return acc;
        }, {});

        return Object.keys(stats).map(name => ({
            name,
            average: filters.currency === "USD"
                ? (stats[name].sum / stats[name].count).toFixed(2)
                : Math.round(stats[name].sum / stats[name].count),
            count: stats[name].count
        }));
    }, [reports, filters.currency]);

    // تجهيز بيانات الرسم البياني
    const chartData = useMemo(() => {
        if (!reports || reports.length === 0) return [];
        return [...reports]
            .sort((a, b) => new Date(a.capturedAtUtc) - new Date(b.capturedAtUtc))
            .map(item => ({
                timestamp: new Date(item.capturedAtUtc).getTime(),
                price: item.priceValue,
                product: item.productName,
                fullDate: new Date(item.capturedAtUtc).toLocaleDateString('ar-SY')
            }));
    }, [reports]);

    const loadEstablishments = async (inputValue) => {
        if (!inputValue) return [];
        try {
            const res = await backEnd_request.get(`/Establishments?q=${inputValue}&page=1&size=20`);
            return res.data.data.map(est => ({
                value: est.fieldUID,
                label: `${est.name} (${est.district || est.city})`
            }));
        } catch (err) { return []; }
    };

    const fetchProducts = useCallback(async () => {
        try {
            const res = await backEnd_request.get("/PriceSurveyProducts?includeInactive=true");
            setProducts(res.data.map(p => ({ value: p.priceSurveyProductId, label: p.nameAr || p.name })));
        } catch (err) { console.error(err); }
    }, [backEnd_request]);

    const fetchReportData = async () => {
        if (isSearchDisabled) return;
        setLoading(true);
        try {
            const queryParams = new URLSearchParams(filters).toString();
            const res = await backEnd_request.get(`/PriceSurveys/report?${queryParams}`);
            setReports(res.data || []);
        } catch (err) {
            console.error(err);
            setReports([]);
        } finally { setLoading(false); }
    };

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const customStyles = {
        control: (base) => ({ ...base, borderRadius: '8px', minHeight: '42px', border: '1px solid #ced4da' })
    };

    return (
        <div className="container-fluid px-4 py-4" dir="rtl" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="fw-bold text-dark m-0"><FaTable className="ms-2 text-primary" /> تقارير سبر الأسعار</h3>
                <button className="btn btn-success shadow-sm d-flex align-items-center fw-bold" disabled={reports.length === 0}>
                    <FaFileExcel className="ms-2" /> تصدير Excel
                </button>
            </div>

            {/* Filters */}
            <div className="card border-0 shadow-sm rounded-4 mb-4">
                <div className="card-body p-4 text-end">
                    <div className="row g-3">
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2 text-danger">من تاريخ *</label>
                            <input type="date" className="form-control" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
                        </div>
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2 text-danger">إلى تاريخ *</label>
                            <input type="date" className="form-control" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
                        </div>
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2"><FaMoneyBillWave className="ms-1 text-success" /> العملة</label>
                            <Select
                                options={currencyOptions}
                                defaultValue={currencyOptions[0]}
                                styles={customStyles}
                                onChange={(opt) => setFilters({ ...filters, currency: opt.value })}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2">المنشأة</label>
                            <AsyncSelect cacheOptions loadOptions={loadEstablishments} placeholder="بحث..." styles={customStyles} isClearable onChange={(opt) => setFilters({ ...filters, establishmentUid: opt?.value || "" })} />
                        </div>
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2">المادة</label>
                            <Select options={products} placeholder="اختر مادة..." isClearable styles={customStyles} onChange={(opt) => setFilters({ ...filters, productId: opt?.value || "" })} />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button className="btn btn-primary w-100 fw-bold shadow-sm" style={{ height: '42px' }} onClick={fetchReportData} disabled={isSearchDisabled || loading}>
                                {loading ? <span className="spinner-border spinner-border-sm"></span> : <><FaSearch className="ms-2" /> بحث</>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {reports.length > 0 && (
                <>
                    {/* بطاقات الإحصائيات */}
                    <div className="row mb-4">
                        {productStats.map((stat, idx) => (
                            <div className="col-md-3 mb-3" key={idx}>
                                <div className="card border-0 shadow-sm rounded-3 bg-white border-start border-success border-4 h-100">
                                    <div className="card-body p-3">
                                        <div className="text-muted small fw-bold mb-1">{stat.name}</div>
                                        <div className="d-flex align-items-baseline">
                                            <h4 className="fw-bold text-success mb-0">{Number(stat.average).toLocaleString()}</h4>
                                            <small className="text-muted ms-2 fw-bold">{filters.currency === "SYP" ? "ل.س" : "$"}</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* الرسم البياني - يظهر عند اختيار مادة محددة فقط ليكون ذا معنى */}
                    {/* الرسم البياني */}
                    {filters.productId && (
                        <div className="card border-0 shadow-sm rounded-4 mb-4 p-4">
                            <h5 className="fw-bold mb-4 text-dark d-flex align-items-center">
                                <FaChartLine className="ms-2 text-info" /> مؤشر تغير السعر ({filters.currency === "SYP" ? "ل.س" : "$"})
                            </h5>
                            <div style={{ width: '100%', height: 350 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                        <XAxis
                                            dataKey="timestamp"
                                            type="number"
                                            domain={['dataMin', 'dataMax']}
                                            tickFormatter={(t) => new Date(t).toLocaleDateString('ar-SY', { day: 'numeric', month: 'short' })}
                                            tick={{ fontSize: 12 }}
                                        />
                                        {/* التعديل هنا: يبدأ من 0 وينتهي عند أعلى قيمة تلقائياً */}
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            domain={[0, 'auto']}
                                            tickFormatter={(value) => value.toLocaleString()}
                                        />
                                        <Tooltip
                                            labelFormatter={(t) => `التاريخ: ${new Date(t).toLocaleString('ar-SY')}`}
                                            formatter={(value) => [`${value.toLocaleString()} ${filters.currency === "SYP" ? "ل.س" : "$"}`, "السعر"]}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="price"
                                            stroke="#0d6efd"
                                            strokeWidth={3}
                                            dot={{ r: 4, fill: '#0d6efd' }}
                                            activeDot={{ r: 8 }}
                                            animationDuration={1000}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}

                    {/* الجدول */}
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0 text-end">
                                <thead className="bg-light text-secondary">
                                    <tr>
                                        <th className="py-3 ps-4">التاريخ والوقت</th>
                                        <th>المنشأة</th>
                                        <th className="text-center">المادة</th>
                                        <th className="text-center">السعر ({filters.currency})</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reports.map((item, index) => (
                                        <tr key={index}>
                                            <td className="ps-4">
                                                <div className="fw-bold">{new Date(item.capturedAtUtc).toLocaleDateString('ar-SY')}</div>
                                                <small className="text-muted"><FaClock className="ms-1" /> {new Date(item.capturedAtUtc).toLocaleTimeString('ar-SY', { hour: '2-digit', minute: '2-digit' })}</small>
                                            </td>
                                            <td>
                                                <div className="fw-bold">{item.establishmentName}</div>
                                                <small className="text-muted">{item.governorate} - {item.city}</small>
                                            </td>
                                            <td className="text-center"><span className="badge bg-primary-subtle text-primary px-3">{item.productName}</span></td>
                                            <td className="text-center">
                                                <div className="fw-bold text-success fs-5">
                                                    {item.priceValue.toLocaleString()}
                                                    <small className="fw-normal text-muted ms-1 small">{filters.currency === "SYP" ? "ل.س" : "$"}</small>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {!loading && reports.length === 0 && (
                <div className="text-center py-5 bg-white rounded-4 shadow-sm border mt-4">
                    <div className="text-muted mb-2 fs-4">لا توجد بيانات متاحة حالياً</div>
                    <div className="small text-muted">يرجى اختيار النطاق الزمني والضغط على بحث</div>
                </div>
            )}
        </div>
    );
};

export default PriceSurveyReportPage;